import { useTranslation } from "react-i18next";
import type { ErrorIdentificationContent } from "../content-types";
import { cx } from "@/components/ui";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

/** Tap the single wrong word in a sentence. Answer is the tapped token id;
 *  graded against the token whose `is_error` is true. */
export function ErrorIdentificationRenderer({
  content,
  answer,
  setAnswer,
  revealed,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as ErrorIdentificationContent;
  const tokens = c.sentence_tokens ?? [];
  const picked = answer as string | undefined;

  return (
    <div>
      <Prompt>{c.prompt || t("Tap the word that's wrong")}</Prompt>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {tokens.map((t) => {
          const isPicked = picked === t.id;
          let cls = "border-black/10 bg-white hover:border-brand-300 text-ink";
          if (!revealed && isPicked) cls = "border-brand-500 bg-brand-50 text-brand-700";
          if (revealed) {
            if (t.is_error) cls = "border-emerald-400 bg-emerald-50 text-emerald-700";
            else if (isPicked) cls = "border-red-400 bg-red-50 text-red-700";
            else cls = "border-transparent bg-transparent text-ink-soft";
          }
          return (
            <button
              key={t.id}
              type="button"
              disabled={revealed}
              onClick={() => !revealed && setAnswer(t.id)}
              className={cx("rounded-lg border px-2.5 py-1.5 text-sm transition disabled:cursor-default", cls)}
            >
              {t.text}
            </button>
          );
        })}
      </div>
      {revealed && c.correction && (
        <p className="mt-3 text-sm text-emerald-700">
          {t("Correction:")} <span className="font-semibold">{c.correction}</span>
        </p>
      )}
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
